import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Download, ExternalLink, FileText, FolderSync, Image as ImageIcon, Mail, MailOpen, Paperclip, Plus, RefreshCw, Search, Trash2, Upload, X } from 'lucide-react';
import { api } from '../api.ts';
import { AuthorizeGoogle } from '../components/AuthorizeGoogle.tsx';
import { useDashboard, useSave } from '../data.ts';
import { fmtDate } from '../util.ts';
import { ACCEPT, prepare, toForm } from '../attach.ts';
import { Empty, Err } from '../components/ui.tsx';

type Source = { type: 'drive' | 'gmail' | 'upload' | 'note'; ref: string; url: string; label: string };
type Expense = {
  id: string; date: string | null; vendor: string; order_id: string; subtotal_cents: number | null; tax_cents: number | null; shipping_cents: number | null;
  discount_cents: number | null; total_cents: number | null; currency: string; workstream: string | null; category: string; paid_by: string; payment_method: string;
  linked_task_ids: string[]; line_items: { description: string; quantity: number | null; amount_cents: number | null }[]; source_links: Source[];
  status: 'verified' | 'needs_review'; review_reasons: string[];
  documents?: { id: string; kind: 'pdf' | 'image' | 'email'; name: string; size: number; source: string }[];
};
type Fin = {
  expenses: Expense[]; categories: string[]; payers: string[]; settings: { drive_folder_id: string; drive_folder_name: string };
  kpis: { ytd_cents: number; month_cents: number; hw_rnd_cents: number; founders: { travis_cents: number; erick_cents: number }; needs_review: number; non_usd: number; by_workstream: Record<string, number> };
};

// Display only; all sums arrive from the server in integer cents.
const money = (c: number | null, cur = 'USD') => c === null ? '—'
  : `${cur === 'USD' ? '$' : `${cur} `}${c < 0 ? '-' : ''}${Math.floor(Math.abs(c) / 100).toLocaleString('en-US')}.${String(Math.abs(c) % 100).padStart(2, '0')}`;
// "1,535.07" → 153507 without floating point.
function parseCents(s: string): number | null {
  const t = s.replace(/[^\d.\-]/g, '');
  if (!t || !/\d/.test(t)) return null;
  const neg = t.startsWith('-');
  const [i, f = ''] = t.replace('-', '').split('.');
  return (neg ? -1 : 1) * (Number(i || '0') * 100 + Number((f + '00').slice(0, 2)));
}
const sourceIcon = { drive: FolderSync, gmail: Mail, upload: Paperclip, note: Paperclip } as const;
const docIcon = { pdf: FileText, image: ImageIcon, email: MailOpen } as const;
const docLabel = { pdf: 'PDF', image: 'Image', email: 'Email' } as const;

export function FinancialsPage() {
  const q = useQuery({ queryKey: ['financials'], queryFn: () => api<Fin>('/financials') });
  const d = useDashboard();
  const save = useSave();
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ text: '', workstream: '', category: '', paid_by: '', status: '' });
  const [folder, setFolder] = useState('');
  const picker = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const ready = (await Promise.all(files.slice(0, 3).map(prepare))).filter((p) => !p.error);
      return api<{ results: { name: string; skipped?: boolean; created?: boolean; error?: string }[] }>('/expenses/upload', { method: 'POST', body: toForm({}, ready) });
    },
    onSuccess: () => qc.invalidateQueries(),
  });
  // One click syncs everything: each request reads what it can in ~2 minutes, and we keep asking while a source has more.
  const [progress, setProgress] = useState('');
  const sync = useMutation({
    mutationFn: async () => {
      const total: Record<string, any> = {};
      const want = { drive: true, gmail: true, docs: true };
      for (let round = 0; round < 15 && (want.drive || want.gmail || want.docs); round++) {
        const r = await api<Record<string, any>>('/financials/sync', { method: 'POST', body: want });
        if (r.docs) {
          const t = total.docs ?? { checked: 0, attached: 0, unavailable: 0, failed: 0, errors: [] };
          total.docs = r.docs.error ? r.docs : { ...r.docs, checked: t.checked + r.docs.checked, attached: t.attached + r.docs.attached, unavailable: t.unavailable + r.docs.unavailable,
            failed: t.failed + r.docs.failed, errors: [...t.errors, ...r.docs.errors] };
          want.docs = !r.docs.error && !!r.docs.more;
        }
        for (const k of ['drive', 'gmail'] as const) {
          if (!r[k]) continue;
          if (r[k].error) { total[k] = r[k]; want[k] = false; continue; }
          const t = total[k] ?? { checked: 0, added: 0, merged: 0, skipped: 0, failed: 0, errors: [] };
          total[k] = { ...r[k], checked: t.checked + r[k].checked, added: t.added + r[k].added, merged: t.merged + r[k].merged, skipped: t.skipped + r[k].skipped,
            failed: t.failed + r[k].failed, errors: [...t.errors, ...r[k].errors] };
          want[k] = !!r[k].more;
        }
        qc.invalidateQueries();
        const left = (['drive', 'gmail'] as const).reduce((n, k) => n + (want[k] ? total[k]?.remaining ?? 0 : 0), 0);
        const read = (total.drive?.checked ?? 0) + (total.gmail?.checked ?? 0);
        setProgress(want.drive || want.gmail ? `Read ${read} so far · ${left} to go…` : want.docs ? 'Fetching receipt files…' : '');
      }
      return total;
    },
    onSettled: () => { setProgress(''); qc.invalidateQueries(); },
  });
  const setFolderM = useMutation({ mutationFn: (f: string) => api('/financials/settings', { method: 'PUT', body: { folder: f } }), onSuccess: () => { setFolder(''); qc.invalidateQueries(); } });

  const projects = d.data?.projects ?? [];
  const tasks = d.data?.tasks ?? [];
  const rows = useMemo(() => (q.data?.expenses ?? []).filter((e) =>
    (!filters.workstream || (e.workstream ?? '') === filters.workstream) && (!filters.category || e.category === filters.category) &&
    (!filters.paid_by || e.paid_by === filters.paid_by) && (!filters.status || (filters.status === 'no_receipt' ? !e.documents?.length : e.status === filters.status)) &&
    (!filters.text || `${e.vendor} ${e.order_id} ${e.line_items.map((l) => l.description).join(' ')}`.toLowerCase().includes(filters.text.toLowerCase()))), [q.data, filters]);
  if (q.error) return <div className="page"><Err e={q.error} /></div>;
  if (!q.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const { kpis: k } = q.data;
  const edit = (id: string, body: object) => save.mutate({ path: `/expenses/${id}`, method: 'PATCH', body });
  const bars = Object.entries(k.by_workstream).sort((a, b) => b[1] - a[1]);
  const maxBar = Math.max(1, ...bars.map(([, v]) => v));
  const projName = (id: string) => projects.find((p) => p.id === id)?.title ?? (id === 'unassigned' ? 'Unassigned' : id);

  return (
    <div className="page fin">
      <header className="page-h row">
        <div><h1>Financials</h1><p className="muted">Company spend, R&amp;D cost and founder out-of-pocket. Amounts are read from receipts and added up in code, to the cent.</p></div>
        <span className="spacer" />
        <a className="btn" href="/api/financials/export.csv"><Download size={14} aria-hidden /> Export CSV for CPA</a>
      </header>

      <div className="kpis">
        <div className="kpi"><b>{money(k.ytd_cents)}</b><span>Total spend {new Date().getFullYear()}</span></div>
        <div className="kpi"><b>{money(k.month_cents)}</b><span>Spend this month</span></div>
        <div className="kpi"><b>{money(k.hw_rnd_cents)}</b><span>Hardware &amp; R&amp;D (HW + REG)</span></div>
        <div className="kpi"><b>{money(k.founders.travis_cents + k.founders.erick_cents)}</b><span>Founder out-of-pocket · Travis {money(k.founders.travis_cents)} · Erick {money(k.founders.erick_cents)}</span></div>
        <div className={`kpi ${k.needs_review ? 'bad' : ''}`}><b>{k.needs_review}</b><span>Needs review</span></div>
      </div>

      <div className="fin-grid">
        <section className="card">
          <div className="card-h"><h2>Spend by workstream, {new Date().getFullYear()}</h2>{k.non_usd > 0 && <span className="muted sm">{k.non_usd} non-USD not included</span>}</div>
          {bars.length ? (
            <ul className="hbars" aria-label="Spend by workstream">
              {bars.map(([ws, v]) => (
                <li key={ws} title={`${projName(ws)}: ${money(v)}`}>
                  <span className="hb-label">{projName(ws)}</span>
                  <span className="hb-track"><i style={{ width: `${(v / maxBar) * 100}%` }} /></span>
                  <span className="hb-value">{money(v)}</span>
                </li>
              ))}
            </ul>
          ) : <Empty>No spend recorded this year yet.</Empty>}
        </section>

        <section className="card">
          <div className="card-h"><h2>Bring in receipts</h2></div>
          <div className="dropzone" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload.mutate([...e.dataTransfer.files].filter((f) => ACCEPT.includes(f.type))); }}
            onClick={() => picker.current?.click()} role="button" tabIndex={0}>
            <Upload size={16} aria-hidden /> {upload.isPending ? 'Reading receipts…' : 'Drop receipts or invoices here, or click to choose (PDF, PNG, JPEG, WebP)'}
            <input ref={picker} type="file" hidden multiple accept={ACCEPT.join(',')} onChange={(e) => { upload.mutate([...(e.target.files ?? [])]); e.target.value = ''; }} />
          </div>
          {upload.data && <p className="sm">{upload.data.results.map((r) => `${r.name}: ${r.error ?? (r.skipped ? 'not a receipt' : r.created ? 'added' : 'merged with an existing record')}`).join(' · ')}</p>}
          <div className="sync-row">
            <div>
              <b className="sm">Drive receipts folder</b>
              <div className="muted sm">{q.data.settings.drive_folder_name || 'Not set'}</div>
            </div>
            <form className="row" onSubmit={(e) => { e.preventDefault(); if (folder.trim()) setFolderM.mutate(folder.trim()); }}>
              <input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="Paste a Drive folder link" aria-label="Drive receipts folder link" />
              <button className="btn sm" disabled={!folder.trim() || setFolderM.isPending}>Set</button>
            </form>
          </div>
          <button className="btn primary" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw size={14} className={sync.isPending ? 'spin' : ''} aria-hidden /> {sync.isPending ? progress || 'Syncing Drive & Gmail…' : 'Sync Drive & Gmail receipts'}
          </button>
          {sync.data && (
            <ul className="sync-report">
              {(['drive', 'gmail'] as const).map((s) => { const r = sync.data[s]; if (!r) return null; return (
                <li key={s}><b>{s === 'drive' ? 'Drive' : 'Gmail'}:</b> {r.error ? <><span className="err">{r.error}</span>{/Authorize|authorized/.test(r.error) && <> <AuthorizeGoogle back="/financials" /></>}</>
                  : `${s === 'drive' ? `scanned ${r.folders ?? 1} folder${r.folders === 1 ? '' : 's'} · ` : ''}${r.checked} read · ${r.added} added · ${r.merged} merged · ${r.skipped} not receipts${r.failed ? ` · ${r.failed} failed` : ''}${r.more ? ' · more remain, sync again' : ''}`}
                  {!r.error && r.errors?.length > 0 && <ul className="muted sm">{r.errors.slice(0, 3).map((e: string) => <li key={e}>{e}</li>)}</ul>}</li>
              ); })}
              {sync.data.docs && (sync.data.docs.error ? <li><b>Receipt files:</b> <span className="err">{sync.data.docs.error}</span></li>
                : sync.data.docs.checked > 0 && <li><b>Receipt files:</b> attached to {sync.data.docs.attached} expense{sync.data.docs.attached === 1 ? '' : 's'}
                  {sync.data.docs.unavailable ? ` · ${sync.data.docs.unavailable} not reachable from your Google account (the partner who synced them can fetch them, or attach by hand)` : ''}
                  {sync.data.docs.failed ? ` · ${sync.data.docs.failed} failed` : ''}</li>)}
            </ul>
          )}
          <Err e={upload.error ?? sync.error ?? setFolderM.error} />
        </section>
      </div>

      <section className="card flush">
        <div className="ledger-filters">
          <div className="search"><Search size={14} aria-hidden /><input value={filters.text} onChange={(e) => setFilters({ ...filters, text: e.target.value })} placeholder="Search vendor, order number, item" aria-label="Search expenses" /></div>
          <select value={filters.workstream} onChange={(e) => setFilters({ ...filters, workstream: e.target.value })} aria-label="Filter workstream"><option value="">All workstreams</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.key}</option>)}</select>
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} aria-label="Filter category"><option value="">All categories</option>{q.data.categories.map((c) => <option key={c}>{c}</option>)}</select>
          <select value={filters.paid_by} onChange={(e) => setFilters({ ...filters, paid_by: e.target.value })} aria-label="Filter payer"><option value="">Anyone paid</option>{q.data.payers.map((c) => <option key={c}>{c}</option>)}</select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} aria-label="Filter status"><option value="">Any status</option><option value="verified">Verified</option><option value="needs_review">Needs review</option><option value="no_receipt">Missing receipt file</option></select>
          <span className="muted sm">{rows.length} of {q.data.expenses.length}</span>
        </div>
        <div className="table-scroll">
          <table className="table ledger">
            <thead><tr><th>Date</th><th>Vendor</th><th>Order / invoice</th><th>Category</th><th>Workstream</th><th>Paid by</th><th className="num">Total</th><th>Status</th><th>Linked task</th><th>Receipt</th><th /></tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className={e.status === 'needs_review' ? 'review' : ''}>
                  <td><input type="date" defaultValue={e.date ?? ''} aria-label="Date" onBlur={(x) => x.target.value !== (e.date ?? '') && edit(e.id, { date: x.target.value || null })} /></td>
                  <td><input defaultValue={e.vendor} aria-label="Vendor" onBlur={(x) => x.target.value !== e.vendor && edit(e.id, { vendor: x.target.value })} /></td>
                  <td><input defaultValue={e.order_id} aria-label="Order number" className="mono" onBlur={(x) => x.target.value !== e.order_id && edit(e.id, { order_id: x.target.value })} /></td>
                  <td><select value={e.category} aria-label="Category" onChange={(x) => edit(e.id, { category: x.target.value })}>{q.data!.categories.map((c) => <option key={c}>{c}</option>)}</select></td>
                  <td><select value={e.workstream ?? ''} aria-label="Workstream" onChange={(x) => edit(e.id, { workstream: x.target.value || null })}><option value="">—</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.key}</option>)}</select></td>
                  <td><select value={e.paid_by} aria-label="Paid by" onChange={(x) => edit(e.id, { paid_by: x.target.value })}>{q.data!.payers.map((c) => <option key={c}>{c}</option>)}</select></td>
                  <td className="num"><input defaultValue={e.total_cents === null ? '' : money(e.total_cents, e.currency)} aria-label="Total" title={breakdown(e)}
                    onBlur={(x) => { const c = parseCents(x.target.value); if (c !== e.total_cents) edit(e.id, { total_cents: c }); }} /></td>
                  <td>
                    <button className={`pill ${e.status === 'verified' ? 's-done' : 's-blocked'}`} title={e.review_reasons.join('\n') || 'Totals reconcile to the cent'}
                      onClick={() => edit(e.id, { status: e.status === 'verified' ? 'needs_review' : 'verified' })}>
                      {e.status === 'verified' ? <><CheckCircle2 size={11} aria-hidden /> Verified</> : <><AlertCircle size={11} aria-hidden /> Needs review</>}
                    </button>
                  </td>
                  <td><select value={e.linked_task_ids[0] ?? ''} aria-label="Linked task" onChange={(x) => edit(e.id, { linked_task_ids: x.target.value ? [x.target.value] : [] })}>
                    <option value="">—</option>{tasks.filter((t) => !e.workstream || t.project === e.workstream).map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}</select></td>
                  <td className="rcpt"><Receipts e={e} /></td>
                  <td><button className="icon" aria-label={`Delete ${e.vendor} expense`} onClick={() => confirm(`Delete the ${e.vendor} expense?`) && save.mutate({ path: `/expenses/${e.id}`, method: 'DELETE' })}><Trash2 size={13} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <Empty>{q.data.expenses.length ? 'No expenses match the filters.' : 'No expenses yet. Drop a receipt, sync Drive and Gmail, or paste an invoice into ⌘K.'}</Empty>}
        </div>
        <Err e={save.error} />
      </section>
    </div>
  );
}

// The receipt files kept with an expense (open in a new tab), where it came from, and a button to attach one by hand.
function Receipts({ e }: { e: Expense }) {
  const qc = useQueryClient();
  const picker = useRef<HTMLInputElement>(null);
  const attach = useMutation({
    mutationFn: async (files: File[]) => {
      const ready = (await Promise.all(files.slice(0, 3).map(prepare))).filter((p) => !p.error);
      if (!ready.length) throw new Error('Choose a PDF, PNG, JPEG or WebP file.');
      return api<{ added: number }>(`/expenses/${e.id}/documents`, { method: 'POST', body: toForm({}, ready) });
    },
    onSuccess: () => qc.invalidateQueries(),
  });
  const remove = useMutation({ mutationFn: (doc: string) => api(`/expenses/${e.id}/documents/${doc}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries() });
  const docs = e.documents ?? [];
  return (
    <div className="receipts" onDragOver={(x) => x.preventDefault()} onDrop={(x) => { x.preventDefault(); attach.mutate([...x.dataTransfer.files].filter((f) => ACCEPT.includes(f.type))); }}>
      {docs.map((d) => { const Icon = docIcon[d.kind]; return (
        <span key={d.id} className="doc-chip">
          <a href={`/api/expenses/${e.id}/documents/${d.id}`} target="_blank" rel="noreferrer" title={`${d.name} · ${(d.size / 1024).toFixed(0)} KB`}><Icon size={13} aria-hidden /> {docLabel[d.kind]}</a>
          <button className="icon" aria-label={`Remove ${d.name}`} onClick={() => confirm(`Remove ${d.name} from this expense?`) && remove.mutate(d.id)}><X size={11} /></button>
        </span>
      ); })}
      {e.source_links.map((s, i) => { const Icon = sourceIcon[s.type]; return s.url && s.type !== 'upload'
        ? <a key={i} className="muted" href={s.url} target="_blank" rel="noreferrer" title={`Original: ${s.label}`}><Icon size={13} aria-label={s.label} /></a> : null; })}
      <button className={`icon${docs.length ? '' : ' attach-missing'}`} title={docs.length ? 'Attach another file' : 'No receipt file yet: attach one (or drop it here)'}
        aria-label={`Attach a receipt to the ${e.vendor} expense`} onClick={() => picker.current?.click()} disabled={attach.isPending}>
        {attach.isPending ? <RefreshCw size={12} className="spin" /> : docs.length ? <Plus size={12} /> : <><Paperclip size={12} /> Attach</>}
      </button>
      <input ref={picker} type="file" hidden style={{ display: 'none' }} multiple accept={ACCEPT.join(',')} onChange={(x) => { attach.mutate([...(x.target.files ?? [])]); x.target.value = ''; }} />
      {(attach.error || remove.error) && <span className="err sm">{(attach.error ?? remove.error as Error).message}</span>}
    </div>
  );
}

function breakdown(e: Expense) {
  const parts = [
    e.subtotal_cents !== null && `Subtotal ${money(e.subtotal_cents, e.currency)}`, e.tax_cents !== null && `Tax ${money(e.tax_cents, e.currency)}`,
    e.shipping_cents !== null && `Shipping ${money(e.shipping_cents, e.currency)}`, e.discount_cents !== null && `Discount ${money(e.discount_cents, e.currency)}`,
    ...e.line_items.map((l) => `• ${l.description}${l.quantity ? ` ×${l.quantity}` : ''}: ${money(l.amount_cents, e.currency)}`),
  ].filter(Boolean);
  return parts.join('\n') || 'No breakdown on the receipt';
}

void ExternalLink; void fmtDate;
